import * as React from 'react';
import * as Yup from 'yup';
import propz from 'propz';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { AppUser } from 'Src/views/App/App';
import { changePassword } from 'Src/helpers/service/nobody/password';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import { Loader } from '../../../../components/Loader/Loader';
import './ChangePasswordTab.scss';

interface Props {
  user: AppUser;
}

interface State {
  isErrorModalOpen: boolean;
  isSuccessModalOpen: boolean;
  isLoading: boolean;
  errorText: string;
}

export class ChangePasswordTab extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isErrorModalOpen: false,
      isSuccessModalOpen: false,
      isLoading: false,
      errorText: ''
    };
  }

  onSubmit = data => {
    const { user } = this.props;
    const { oldPassword, newPassword } = data;

    this.setState({ isLoading: true });

    changePassword(user, oldPassword, newPassword)
      .then(() => {
        this.setState({ isLoading: false, isSuccessModalOpen: true });
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], 'Unable to change password.');
        this.setState({ isLoading: false, isErrorModalOpen: true, errorText });
      });
  };

  closeErrorModal = () => {
    this.setState({ isErrorModalOpen: false });
  };

  renderErrorModal() {
    const { isErrorModalOpen, errorText } = this.state;
    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title={'Password not changed'}
        body={errorText}
        buttonCancelText={'Close'}
        onCloseClick={this.closeErrorModal}
        customClass={'upProfileCompactModal'}
      />
    );
  }

  closeSuccessModal = () => {
    this.setState({ isSuccessModalOpen: false });
  };

  renderSuccessModal() {
    const { isSuccessModalOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isSuccessModalOpen}
        title={'Password changed'}
        body={'Your password has been successfully changed.'}
        buttonCancelText={'Ok'}
        onCloseClick={this.closeSuccessModal}
        customClass={'upProfileCompactModal'}
      />
    );
  }

  renderField = (name: string, label: string, errors, touched) => {
    const hasError = touched[name] && errors[name];

    return (
      <div className="upPasswordField">
        <div className="upPasswordLabelRow">
          <label className={hasError ? 'upProfileLabel upProfileLabelError' : 'upProfileLabel'}>{label}</label>
          <ErrorMessage component="span" className="upProfileErrorText" name={name} />
        </div>
        <Field
          type="password"
          name={name}
          className={hasError ? 'upPasswordInput mChangePasswordInputWithError' : 'upPasswordInput'}
        />
      </div>
    );
  };

  render() {
    const changePasswordForm = {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    };

    const changePasswordSchema = Yup.object().shape({
      oldPassword: Yup.string().required('please enter current password'),
      newPassword: Yup.string()
        .required('please enter new password')
        .test(
          'newPassword',
          `password must contain at least one capital, lower case character, one number and must be 8 digits long`,
          value => {
            const passwordRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
            return passwordRegExp.test(value);
          }
        ),
      confirmNewPassword: Yup.string()
        .required('please confirm new password')
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    });

    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="upPasswordTab">
        {this.renderErrorModal()}
        {this.renderSuccessModal()}
        <section className="upProfileCard">
          <header className="upProfileCardHeader">
            <div>
              <h2 className="upProfileCardTitle">Change password</h2>
              <p className="upProfileCardSub">Use a strong password you don't use anywhere else.</p>
            </div>
          </header>
          <div className="upProfileCardBody">
            <Formik initialValues={changePasswordForm} validationSchema={changePasswordSchema} onSubmit={this.onSubmit}>
              {({ errors, touched }) => (
                <Form className="upPasswordForm">
                  {this.renderField('oldPassword', 'Current password', errors, touched)}
                  {this.renderField('newPassword', 'New password', errors, touched)}
                  {this.renderField('confirmNewPassword', 'Confirm new password', errors, touched)}
                  <button type="submit" className="upProfileBtn upProfileBtnPrimary upPasswordSave">
                    Save
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </section>
      </div>
    );
  }
}